import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { getStableIds as getStableIdsCore, type FPResult } from './deviceCoreId';
import { getContext } from './utils';

export interface FingerprintData {
  visitorId: string;
  requestId: string;
  confidence: number;
  timestamp: number;
  components: Record<string, any>;
  deviceCoreId?: string; // Optional stable device identifier
}

export interface FingerprintConfig {
  delayFallback?: number;
  debug?: boolean;
}

class FingerprintService {
  private fpPromise: Promise<any> | null = null;
  private config: FingerprintConfig;
  private cachedStableIds: { visitorId: string; deviceCoreId: string } | null = null;

  constructor(config: FingerprintConfig = {}) {
    this.config = {
      delayFallback: 50,
      debug: false,
      ...config
    };
  }

  /**
   * Initialize the fingerprint library
   * This should be called once when the service is first used
   */
  private async initializeFingerprint(): Promise<any> {
    if (!this.fpPromise) {
      this.fpPromise = FingerprintJS.load({
        delayFallback: this.config.delayFallback,
        debug: this.config.debug
      });
    }
    return this.fpPromise;
  }

  /**
   * Get the browser fingerprint
   * Always calculates fresh fingerprint (no caching)
   */
  async getFingerprint(forceRefresh: boolean = false): Promise<FingerprintData> {
    try {
      // Initialize and get fresh fingerprint every time
      const fp = await this.initializeFingerprint();
      const result = await fp.get();
      
      // Create fingerprint data object
      const fingerprintData: FingerprintData = {
        visitorId: result.visitorId,
        requestId: result.requestId,
        confidence: result.confidence?.score || 0,
        timestamp: Date.now(),
        components: result.components || {}
      };

      return fingerprintData;
    } catch (error) {
      console.error('FingerprintService: Error getting fingerprint:', error);
      throw new Error(`Failed to get fingerprint: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }



  /**
   * Check if the current environment supports fingerprinting
   */
  isSupported(): boolean {
    try {
      // Basic checks for required APIs
      return typeof window !== 'undefined' && 
             typeof document !== 'undefined' && 
             typeof navigator !== 'undefined';
    } catch {
      return false;
    }
  }

  /**
   * Clear cached stable IDs
   */
  clearCache(): void {
    this.cachedStableIds = null;
    console.log('Cleared cached stable IDs');
  }



  /**
   * Get both visitorId and stable deviceCoreId
   * Uses session cache but generates fresh on first call or force refresh
   */
  async getStableIds(forceRefresh: boolean = false): Promise<{ visitorId: string; deviceCoreId: string }> {
    try {
      // Return cached stable IDs if available and not forcing refresh
      if (!forceRefresh && this.cachedStableIds) {
        return this.cachedStableIds;
      }

      const fingerprint = await this.getFingerprint(forceRefresh);
      const fpResult: FPResult = {
        visitorId: fingerprint.visitorId,
        components: fingerprint.components
      };

      const stableIds = await getStableIdsCore(fpResult);
      
      // Cache the stable IDs for this session
      this.cachedStableIds = stableIds;
      
      return stableIds;
    } catch (error) {
      console.error('FingerprintService: Error getting stable IDs:', error);
      throw new Error(`Failed to get stable IDs: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

// Create and export a default instance
export const fingerprintService = new FingerprintService();

// Export the class for custom instances
export { FingerprintService };

// Convenience functions for quick access
export const isFingerprintSupported = () => fingerprintService.isSupported();
export const getStableIds = (forceRefresh?: boolean) => fingerprintService.getStableIds(forceRefresh);

// Re-export utilities from deviceCoreId module
export { getStableIds as getStableIdsFromResult } from './deviceCoreId';
